// src/components/authorizations/BrevoConnectionForm.tsx
//
// Brevo auth-create wizard (PII-vault redesign — supersedes ADR-0041).
// Single-form flow: API key + Test connection + name. Brevo destinations
// resolve email_hash → plaintext via the PII vault at outbound, so no
// EXT_ID strategy choice / salt-display is needed at authorization time.
//
// Rendered inline in the setup wizard's modal (a drawer launched from a modal
// sits under the modal's overlay and focus trap, so its fields cannot be
// used), and in a drawer on the Authorizations page.
import { useState } from "react";
import {
  Alert,
  Button,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  testBrevoCredentials,
  createBrevoAuthorization,
} from "@/api/authorizations";

type Props = {
  /** Called after the connection was created. */
  onCreated: () => void;
  /** Shown as a Cancel button when set. */
  onCancel?: () => void;
};

export function BrevoConnectionForm({ onCreated, onCancel }: Props) {
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    account_email: string;
    company_name: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleTest = async () => {
    if (!apiKey.trim()) {
      notifications.show({ color: "red", message: "API key is required" });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testBrevoCredentials(apiKey.trim());
      setTestResult(result);
      if (!name.trim() && result.company_name) {
        setName(result.company_name);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Connection test failed";
      notifications.show({ color: "red", title: "Test failed", message });
    } finally {
      setTesting(false);
    }
  };

  const canSubmit = !!testResult && !!name.trim() && !!apiKey.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createBrevoAuthorization({
        name: name.trim(),
        apiKey: apiKey.trim(),
      });
      notifications.show({
        color: "green",
        message: "Brevo authorization created",
      });
      onCreated();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create authorization";
      notifications.show({ color: "red", title: "Error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Enter your Brevo API key. You can find it under SMTP &amp; API
        &rsaquo; API Keys in your Brevo dashboard.
      </Text>

      <PasswordInput
        label="API key"
        placeholder="xkeysib-..."
        value={apiKey}
        onChange={(e) => {
          setApiKey(e.currentTarget.value);
          setTestResult(null);
        }}
        required
      />

      <Button
        variant="light"
        onClick={handleTest}
        loading={testing}
        disabled={!apiKey.trim()}
      >
        Test connection
      </Button>

      {testResult && (
        <Alert
          icon={<IconCheck size={16} />}
          color="green"
          variant="light"
        >
          Connected to <strong>{testResult.company_name}</strong>{" "}
          ({testResult.account_email}).
        </Alert>
      )}

      <TextInput
        label="Authorization name"
        placeholder="My Brevo account"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />

      <Group justify="flex-end" mt="md">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
        >
          Create authorization
        </Button>
      </Group>
    </Stack>
  );
}
